import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, MapPin, Clock } from 'lucide-react';
import axios from 'axios';
import { PageSEO } from '../components/PageSEO';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { SeasonHighlight } from '../components/MarketFeatures';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MarketPage = () => {
  const [products, setProducts] = useState([]);
  const [producers, setProducers] = useState([]);
  const [filter, setFilter] = useState('all');
  const { addItem } = useCart();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, producersRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/producers`)
      ]);
      setProducts(productsRes.data);
      setProducers(producersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const filteredProducts = filter === 'all' 
    ? products 
    : products.filter(p => p.category === filter);

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'bal', label: 'Bal' },
    { id: 'arı-urunleri', label: 'Arı Ürünleri' },
    { id: 'kozmetik', label: 'Doğal Kozmetik' },
    { id: 'el-yapimi', label: 'El Yapımı' },
    { id: 'hediye', label: 'Hediye Kutuları' }
  ];

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} sepete eklendi!`);
  };

  return (
    <div data-testid="market-page" className="grain-overlay relative">
      <PageSEO title="Ekosistem Marketi" description="KOSAD Ekosistem Marketi - Doğal bal, propolis, balmumu ve arı ürünleri. Kadın üreticilerden doğrudan sofranıza." path="/market" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Ekosistem Marketi</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-6">
              Doğadan Sofranıza
            </h1>
            <p className="text-base text-white/80">
              KOSAD üreticilerinin özenle hazırladığı, doğal ve sürdürülebilir ürünler. 
              Her satın alma ile kadın üreticileri ve doğayı destekliyorsunuz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Banner */}
      <section className="bg-gradient-to-r from-[#D4AF37] to-[#c9a432] py-6">
        <div className="container-custom">
          <motion.div 
            className="flex flex-wrap justify-center gap-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {[
              { value: '%100', label: 'Doğal Ürünler' },
              { value: '25+', label: 'Kadın Üretici' },
              { value: 'Ücretsiz', label: '500₺ Üzeri Kargo' }
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <p className="text-2xl font-semibold text-[#1A3625]">{item.value}</p>
                <p className="text-base text-[#1A3625]/80">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Producers Section */}
      <section className="section-padding bg-white" data-testid="producers-section">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Üreticilerimiz</span>
            <h2 className="text-3xl md:text-4xl text-[#1A3625]">
              Hikayelerini Keşfedin
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {producers.map((producer, index) => (
              <motion.div
                key={producer.id}
                className="card-glass text-center group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`producer-card-${producer.id}`}
              >
                <motion.div 
                  className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-6 ring-4 ring-[#D4AF37]/30 shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <img 
                    src={producer.image}
                    alt={producer.name}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <h3 className="text-xl font-medium text-[#1A3625] mb-1">{producer.name}</h3>
                <p className="text-base text-[#D4AF37] mb-3 font-medium">{producer.title}</p>
                <p className="text-base text-[#5C5C5C] mb-2 flex items-center justify-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {producer.location}
                </p>
                <p className="text-base text-[#8C7A6B] mb-5 flex items-center justify-center gap-1">
                  <Clock className="w-4 h-4" />
                  {producer.years_experience} yıl deneyim
                </p>
                <Link 
                  to={`/market/uretici/${producer.id}`}
                  className="text-[#1A3625] font-semibold text-base hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1"
                >
                  Hikayesini Oku →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding" data-testid="products-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Ürünler</span>
              <h2 className="text-3xl md:text-4xl text-[#1A3625]">Tüm Ürünlerimiz</h2>
            </motion.div>
            
            {/* Season Highlight */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="mt-4 md:mt-0"
            >
              <SeasonHighlight />
            </motion.div>
          </div>
          
          {/* Filter */}
          <motion.div 
            className="flex flex-wrap gap-2 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
              {categories.map((cat) => (
                <motion.button
                  key={cat.id}
                  onClick={() => setFilter(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-base font-medium transition-all ${
                    filter === cat.id 
                      ? 'bg-[#1A3625] text-white shadow-lg' 
                      : 'bg-white text-[#5C5C5C] hover:bg-[#E5DAC8] shadow'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.label}
                </motion.button>
              ))}
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                className="card-premium group product-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                layout
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/market/urun/${product.id}`}>
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4 relative">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                    />
                    {!product.in_stock && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm">
                        <span className="text-white font-medium bg-black/50 px-4 py-2 rounded-full">Stokta Yok</span>
                      </div>
                    )}
                    {/* Quick add overlay */}
                    {product.in_stock && (
                      <div className="product-card-overlay absolute inset-0 flex items-end justify-center pb-4">
                        <motion.button
                          onClick={(e) => handleAddToCart(product, e)}
                          className="bg-white text-[#1A3625] px-6 py-3 rounded-full font-medium flex items-center gap-2 hover:bg-[#D4AF37] hover:text-white transition-colors shadow-lg"
                          whileHover={{ scale: 1.05 }}
                          data-testid={`quick-add-${product.id}`}
                        >
                          <ShoppingBag className="w-4 h-4" />
                          Sepete Ekle
                        </motion.button>
                      </div>
                    )}
                  </div>
                </Link>
                
                <Link to={`/market/urun/${product.id}`}>
                  <h3 className="text-lg font-medium text-[#1A3625] mb-1 group-hover:text-[#D4AF37] transition-colors">
                    {product.name}
                  </h3>
                </Link>
                <p className="text-base text-[#5C5C5C] mb-3">{product.unit}</p>
                
                <div className="flex items-center justify-between">
                  <p className="text-xl font-semibold text-[#D4AF37]">₺{product.price}</p>
                  <Link 
                    to={`/market/urun/${product.id}`}
                    className="text-[#1A3625] font-medium text-base hover:text-[#D4AF37] transition-colors"
                  >
                    İncele →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-[#5C5C5C]">Bu kategoride ürün bulunmuyor.</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Gift Box CTA */}
      <section className="section-padding bg-[#1A3625] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="label-uppercase text-[#D4AF37] mb-4 block">Özel Hediye</span>
              <h2 className="text-3xl md:text-4xl text-white mb-6">
                KOSAD Hediye Kutuları
              </h2>
              <p className="text-white/80 text-base mb-8">
                Sevdiklerinize doğanın en değerli hediyelerini sunun. Özel tasarım 
                kutularımızda bal, propolis ve balmumu ürünleri bir arada.
              </p>
              <Link to="/market/urun/hediye-kutusu" className="btn-gold">
                Hediye Kutularını İncele
              </Link>
            </motion.div>
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="image-hover rounded-3xl shadow-2xl overflow-hidden glow-gold">
                <img 
                  src="/images/hikaye-donus.jpg"
                  alt="Hediye Kutusu"
                  className="w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
